import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  Inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { type Icon, IconComponent } from '@examdojo/core/icon';
import { MathInputComponent } from './controls/math-input/math-input.component';
import { SliderComponent } from './controls/slider/slider.component';
import type { ActionDefinition } from './model/widget-action.model';
import { type BaseWidget, type ParamChangeEventData, WidgetEvent } from './model/widget-base.model';
import type { WidgetConfig } from './model/widget-config.model';
import {
  getSliderMax,
  getSliderMin,
  getSliderStep,
  type WidgetButtonControl,
  type WidgetButtonGroupControl,
  type WidgetControl,
  type WidgetControlPosition,
  type WidgetMathInputControl,
  type WidgetSliderControl,
  type WidgetSliderGroupControl,
} from './model/widget-control.model';
import { WIDGET_FACTORY, type WidgetFactory } from './model/widget-factory.model';
import { WidgetOrchestratorService } from './orchestration/widget-orchestrator.service';
import { parseParamPath } from './util/param-path';
import { type BorderConfig, resolveBorderConfig } from './frameworks/jsxgraph/jsxgraph.model';

// Configuration for a widget component instance.
// This extends WidgetConfig and adds Angular component-specific props.
export interface WidgetComponentConfig extends WidgetConfig {
  interactive?: boolean; // Show interactive wrapper with header
  title?: string; // Title for interactive header
}

/** A group of controls that should be rendered together in an input-group */
interface ControlGroup {
  type: 'slider' | 'math-input';
  controls: Array<WidgetSliderControl | WidgetMathInputControl>;
}

/** Either a single control or a group of controls */
type ControlOrGroup = WidgetControl | ControlGroup;

function isControlGroup(item: ControlOrGroup): item is ControlGroup {
  return 'controls' in item && Array.isArray(item.controls);
}

// Generic Angular component that works with any widget framework.
// Automatically selects the appropriate factory based on widget type.
@Component({
  selector: 'dojo-widget',
  standalone: true,
  imports: [NgTemplateOutlet, IconComponent, SliderComponent, MathInputComponent],
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  host: { class: 'block h-full w-full' },
})
export class WidgetComponent implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(WIDGET_FACTORY) private readonly factories: WidgetFactory[],
    private readonly orchestrator: WidgetOrchestratorService,
  ) {
    effect(() => {
      const config = this.config();
      if (this.isInitialized) {
        this.scheduleReinitialize(config);
      }
    });

    // Initialize control states from config
    effect(
      () => {
        const behavior = this.config().behavior;
        const controls = behavior?.controls ?? [];

        const sliderVals: Record<string, number> = {};
        const mathInputVals: Record<string, number> = {};
        const activeButtons: Record<string, string> = {};

        for (const control of controls) {
          if (control.type === 'slider') {
            sliderVals[control.id] = control.defaultValue ?? getSliderMin(control);
          } else if (control.type === 'slider-group') {
            for (const slider of control.sliders) {
              sliderVals[slider.id] = slider.defaultValue ?? getSliderMin(slider);
            }
          } else if (control.type === 'math-input') {
            mathInputVals[control.id] = control.defaultValue ?? 0;
          } else if (control.type === 'button-group' && control.buttons.length > 0) {
            activeButtons[control.id] = control.buttons[0].id;
          }
        }

        this.sliderValues.set(sliderVals);
        this.mathInputValues.set(mathInputVals);
        this.activeButtonInGroup.set(activeButtons);
      },
      { allowSignalWrites: true },
    );
  }

  readonly config = input.required<WidgetComponentConfig>();
  readonly widgetReady = output<BaseWidget>();

  readonly activeButtonInGroup = signal<Record<string, string>>({});
  readonly sliderValues = signal<Record<string, number>>({});
  readonly mathInputValues = signal<Record<string, number>>({});

  readonly borderStyle = computed(() => {
    const display = this.config().display as { canvas?: { border?: BorderConfig } } | undefined;
    const resolved = resolveBorderConfig(display?.canvas?.border);
    if (!resolved.visible) {
      return 'none';
    }
    return `1px solid var(--${resolved.color ?? 'neutral-200'})`;
  });

  readonly topLeftControls = computed(() => this.getGroupedControlsByPosition('top-left'));
  readonly topControls = computed(() => this.getGroupedControlsByPosition('top'));
  readonly topRightControls = computed(() => this.getGroupedControlsByPosition('top-right'));
  readonly bottomLeftControls = computed(() => this.getGroupedControlsByPosition('bottom-left'));
  readonly bottomControls = computed(() => this.getGroupedControlsByPosition('bottom'));
  readonly bottomRightControls = computed(() => this.getGroupedControlsByPosition('bottom-right'));
  readonly sliderFooterControls = computed(() => this.getGroupedControlsByPosition('slider-footer'));
  readonly isFooterMinimal = computed(() => {
    const items = this.sliderFooterControls();
    return items.length > 0 && items.every((item) => this.isMinimalItem(item));
  });

  private readonly widgetContainer = viewChild.required<ElementRef<HTMLDivElement>>('widgetContainer');
  private widget: BaseWidget | null = null;
  private widgetId = `widget-${Math.random().toString(36).slice(2, 11)}`;
  private isInitialized = false;
  private isDestroyed = false;
  private reinitializationInProgress = false;
  private pendingConfig: WidgetComponentConfig | null = null;
  private paramChangeHandler: ((data: unknown) => void) | null = null;

  async ngAfterViewInit(): Promise<void> {
    await this.initializeWidget();
    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.pendingConfig = null; // Cancel any pending reinitialization

    if (this.widget) {
      // Unsubscribe from param changes
      if (this.paramChangeHandler) {
        this.widget.off(WidgetEvent.ParamChange, this.paramChangeHandler);
        this.paramChangeHandler = null;
      }
      this.orchestrator.unregisterWidget(this.widgetId);
      this.widget.destroy();
      this.widget = null;
    }
  }

  isGroup(item: ControlOrGroup): item is ControlGroup {
    return isControlGroup(item);
  }

  /** True when every slider in a group (or a standalone slider) uses the minimal variant */
  isMinimalItem(item: ControlOrGroup): boolean {
    if (isControlGroup(item)) {
      return item.controls.every((c) => c.type === 'slider' && (c as WidgetSliderControl).variant === 'minimal');
    }
    return item.type === 'slider' && (item as WidgetSliderControl).variant === 'minimal';
  }

  asIcon(icon: string): Icon {
    return icon as Icon;
  }

  shouldShowLabel(control: { icon?: string; showLabel?: boolean; label: string }): boolean {
    if (control.showLabel !== undefined) {
      return control.showLabel;
    }
    return !control.icon;
  }

  getSliderValue(control: WidgetSliderControl): number {
    const values = this.sliderValues();
    return control.id in values ? values[control.id] : control.defaultValue ?? getSliderMin(control);
  }

  getSliderGroupValue(group: WidgetSliderGroupControl, sliderId: string): number {
    const values = this.sliderValues();
    if (sliderId in values) {
      return values[sliderId];
    }
    const slider = group.sliders.find((s) => s.id === sliderId);
    return slider?.defaultValue ?? (slider ? getSliderMin(slider) : 0);
  }

  getSliderMin(control: Pick<WidgetSliderControl, 'range'>): number {
    return getSliderMin(control);
  }

  getSliderMax(control: Pick<WidgetSliderControl, 'range'>): number {
    return getSliderMax(control);
  }

  getSliderStep(control: Pick<WidgetSliderControl, 'range'>): number {
    return getSliderStep(control);
  }

  getMathInputValue(control: WidgetMathInputControl): number {
    const values = this.mathInputValues();
    return control.id in values ? values[control.id] : control.defaultValue ?? 0;
  }

  async onButtonClick(control: WidgetButtonControl): Promise<void> {
    if (!this.widget) {
      return;
    }
    await this.widget.executeAction(control.trigger);
  }

  async onButtonGroupClick(
    group: WidgetButtonGroupControl,
    button: Omit<WidgetButtonControl, 'type' | 'position'>,
  ): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.activeButtonInGroup.update((states) => ({ ...states, [group.id]: button.id }));
    await this.widget.executeAction(button.trigger);
  }

  async onSliderChange(control: WidgetSliderControl, value: number): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.sliderValues.update((vals) => ({ ...vals, [control.id]: value }));

    const action = this.buildUpdateParamAction(control.param, value);
    await this.widget.executeAction(action);
  }

  async onSliderGroupChange(control: WidgetSliderGroupControl, event: { id: string; value: number }): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.sliderValues.update((vals) => ({ ...vals, [event.id]: event.value }));

    const slider = control.sliders.find((s) => s.id === event.id);
    if (slider) {
      const action = this.buildUpdateParamAction(slider.param, event.value);
      await this.widget.executeAction(action);
    }
  }

  async onMathInputChange(control: WidgetMathInputControl, value: number): Promise<void> {
    if (!this.widget) {
      return;
    }

    this.mathInputValues.update((vals) => ({ ...vals, [control.id]: value }));

    const action = this.buildUpdateParamAction(control.param, value);
    await this.widget.executeAction(action);
  }

  /**
   * Public API for direct widget control using the action system.
   */
  async dispatch(action: ActionDefinition): Promise<void> {
    if (!this.widget) {
      throw new Error('Widget not initialized');
    }
    await this.widget.executeAction(action);
  }

  /**
   * Get the underlying widget instance.
   */
  getWidget(): BaseWidget | null {
    return this.widget;
  }

  /**
   * Get the widget ID for use with orchestrator.
   */
  getWidgetId(): string {
    return this.widgetId;
  }

  /**
   * Build an UPDATE_PARAM action from a param path and value.
   * Parses the path to extract element (if present) and param name.
   */
  private buildUpdateParamAction(paramPath: string, value: number): ActionDefinition {
    return {
      action: 'UPDATE_PARAM',
      payload: { param: paramPath, value },
    };
  }

  /**
   * Get controls for a position, grouping adjacent sliders and math-inputs.
   * Adjacent controls of the same type (slider or math-input) are wrapped in a ControlGroup.
   * Default positions: sliders/slider-groups/math-inputs -> 'slider-footer', others -> 'top-right'
   */
  private getGroupedControlsByPosition(position: WidgetControlPosition): ControlOrGroup[] {
    const behavior = this.config().behavior;
    const controls = behavior?.controls ?? [];
    const filtered = controls.filter((c) => {
      const defaultPos: WidgetControlPosition =
        c.type === 'slider' || c.type === 'slider-group' || c.type === 'math-input' ? 'slider-footer' : 'top-right';
      return (c.position ?? defaultPos) === position;
    });

    const result: ControlOrGroup[] = [];
    let currentGroup: ControlGroup | null = null;

    for (const control of filtered) {
      const groupableType = control.type === 'slider' || control.type === 'math-input' ? control.type : null;

      if (groupableType) {
        if (currentGroup && currentGroup.type === groupableType) {
          // Add to existing group
          currentGroup.controls.push(control as WidgetSliderControl | WidgetMathInputControl);
        } else {
          // Start new group
          if (currentGroup) {
            result.push(currentGroup);
          }
          currentGroup = {
            type: groupableType,
            controls: [control as WidgetSliderControl | WidgetMathInputControl],
          };
        }
      } else {
        // Non-groupable control - flush current group and add control
        if (currentGroup) {
          result.push(currentGroup);
          currentGroup = null;
        }
        result.push(control);
      }
    }

    // Don't forget the last group
    if (currentGroup) {
      result.push(currentGroup);
    }

    return result;
  }

  private async initializeWidget(): Promise<void> {
    try {
      const widgetType = this.config().type;

      // Find factory that can handle this widget type
      const factory = this.factories.find((f) => f.has(widgetType));

      if (!factory) {
        throw new Error(
          `No factory found for widget type: '${widgetType}'. Available types: ${this.factories
            .flatMap((f) => f.getRegisteredTypes())
            .join(', ')}`,
        );
      }

      // Load framework instance
      const frameworkInstance = await factory.loadFramework();

      // Check if component was destroyed during async operation
      if (this.isDestroyed) {
        return;
      }

      // Create widget
      const container = this.widgetContainer().nativeElement;
      this.widget = factory.create(widgetType, container, frameworkInstance);

      // Initialize widget with the full config (excluding Angular-specific props)
      const { interactive: _interactive, title: _title, ...widgetConfig } = this.config();
      this.widget.init(widgetConfig as WidgetConfig);

      // Subscribe to param changes from handle drags to sync Angular sliders
      this.syncSlidersFromWidgetParams();

      // Register with orchestrator
      this.orchestrator.registerWidget(this.widgetId, this.widget);
      this.widgetReady.emit(this.widget);
    } catch (error) {
      console.error('Failed to initialize widget:', error);
    }
  }

  /**
   * Sync Angular sliders from widget param changes.
   * When handles are dragged, the widget emits param changes that we use to update slider values.
   */
  private syncSlidersFromWidgetParams(): void {
    if (!this.widget) {
      return;
    }

    this.paramChangeHandler = (data: unknown) => {
      const { elementId, params } = data as ParamChangeEventData;
      const controls = this.config().behavior?.controls ?? [];

      this.sliderValues.update((vals) => {
        const newVals = { ...vals };

        for (const control of controls) {
          if (control.type !== 'slider') {
            continue;
          }

          const parsed = parseParamPath(control.param);
          if (parsed.root === elementId && parsed.leaf in params) {
            newVals[control.id] = params[parsed.leaf];
          }
        }

        return newVals;
      });
    };

    this.widget.on(WidgetEvent.ParamChange, this.paramChangeHandler);
  }

  private scheduleReinitialize(config: WidgetComponentConfig): void {
    if (this.isDestroyed) {
      return;
    }

    if (this.reinitializationInProgress) {
      this.pendingConfig = config;
      return;
    }

    this.performReinitialize(config);
  }

  private async performReinitialize(_config: WidgetComponentConfig): Promise<void> {
    this.reinitializationInProgress = true;

    try {
      if (this.widget) {
        this.orchestrator.unregisterWidget(this.widgetId);
        this.widget.destroy();
        this.widget = null;
      }

      // Abort if component was destroyed during cleanup
      if (this.isDestroyed) {
        return;
      }

      this.widgetId = `widget-${Math.random().toString(36).slice(2, 11)}`;
      await this.initializeWidget();
    } finally {
      this.reinitializationInProgress = false;

      // Only process pending config if component is still alive
      if (this.pendingConfig && !this.isDestroyed) {
        const nextConfig = this.pendingConfig;
        this.pendingConfig = null;
        this.scheduleReinitialize(nextConfig);
      }
    }
  }
}
